/** @format */

/**
 * Langfuse 服务拓扑：基于网关返回的 WorkflowGraph 静态图 + Executions 绘制。
 */

import type {
  TraceAnalysisDetailResponse,
  TraceAnalysisDetailSpan,
  WorkflowGraph,
  WorkflowGraphEdge,
  WorkflowGraphEdgeKind,
  WorkflowGraphNode
} from '../../typescript'
import type { TopologyLayoutDirection, TopologyLayoutOptions } from './types'

export interface LangfuseWorkflowExecutionItem {
  /** WorkflowGraph.Executions 中的 ExecutionIndex（1-based） */
  executionIndex: number
  span: TraceAnalysisDetailSpan
}

export interface LangfuseWorkflowNode {
  nodeId: string
  label: string
  kind: WorkflowGraphNode['Kind']
  /** 当前展示的执行 Span，用于点击联动详情 */
  span?: TraceAnalysisDetailSpan
  /** 按 ExecutionIndex 升序的全部执行记录 */
  executionItems?: LangfuseWorkflowExecutionItem[]
  /** 初始展示的执行次序（1-based，对应 ExecutionIndex） */
  currentExecutionIndex?: number
  executionCount?: number
  totalDurationMs?: number
  x: number
  y: number
}

export interface LangfuseWorkflowEdge {
  from: string
  to: string
  kind: WorkflowGraphEdgeKind
  /** 存在 A→B 与 B→A 时合并为一条边，两端均显示箭头 */
  bidirectional?: boolean
}

export interface LangfuseWorkflowTopology {
  nodes: LangfuseWorkflowNode[]
  edges: LangfuseWorkflowEdge[]
  layoutDirection: TopologyLayoutDirection
}

const VIRTUAL_START = '__start__'
const VIRTUAL_END = '__end__'

export function hasLangfuseWorkflowGraph(
  detail: TraceAnalysisDetailResponse | null | undefined
): detail is TraceAnalysisDetailResponse & { WorkflowGraph: WorkflowGraph } {
  const graph = detail?.WorkflowGraph
  return Boolean(graph?.Nodes?.length && graph.Edges?.length)
}

function resolveWorkflowExecutionItems(
  graph: WorkflowGraph,
  node: WorkflowGraphNode,
  spanMap: Map<string, TraceAnalysisDetailSpan>
): LangfuseWorkflowExecutionItem[] {
  if (node.Kind === 'virtual') return []
  // 同一 workflow 节点可能多次执行，每次对应一个 step 级 Span
  return graph.Executions.filter(item => item.NodeId === node.NodeId)
    .sort((a, b) => a.ExecutionIndex - b.ExecutionIndex)
    .map(item => {
      const span = spanMap.get(item.SpanId)
      return span ? { executionIndex: item.ExecutionIndex, span } : null
    })
    .filter((item): item is LangfuseWorkflowExecutionItem => Boolean(item))
}

export function findWorkflowExecutionItem(
  items: LangfuseWorkflowExecutionItem[],
  executionIndex: number
): LangfuseWorkflowExecutionItem | undefined {
  return items.find(item => item.executionIndex === executionIndex)
}

/** 点击多执行节点时，按 ExecutionIndex 顺序轮询下一次执行 */
export function getNextWorkflowExecutionIndex(
  items: LangfuseWorkflowExecutionItem[],
  current?: number
): number {
  if (!items.length) return 0
  const indexes = items.map(item => item.executionIndex).sort((a, b) => a - b)
  if (current === undefined) return indexes[0]
  const pos = indexes.indexOf(current)
  const nextPos = (pos < 0 ? 0 : pos + 1) % indexes.length
  return indexes[nextPos]
}

function resolveInitialExecutionIndex(executionItems: LangfuseWorkflowExecutionItem[]): number {
  if (!executionItems.length) return 0
  // 初始化始终展示第一次执行（如 1/6），不使用 ActiveExecutionIndex
  return executionItems[0].executionIndex
}

/** 从 __start__ 向前分层；回路边（目标层已 ≤ 源层）不参与加深 */
function assignWorkflowLayers(nodeIds: string[], edges: WorkflowGraphEdge[]): Map<string, number> {
  const layers = new Map<string, number>()
  const startId = nodeIds.includes(VIRTUAL_START) ? VIRTUAL_START : nodeIds[0]
  if (!startId) return layers

  layers.set(startId, 0)

  const maxPasses = Math.max(nodeIds.length * 2, 4)
  for (let pass = 0; pass < maxPasses; pass += 1) {
    let changed = false
    for (const edge of edges) {
      const fromLayer = layers.get(edge.From)
      if (fromLayer === undefined) continue

      const existingTo = layers.get(edge.To)
      if (existingTo !== undefined && existingTo <= fromLayer) continue

      const nextLayer = fromLayer + 1
      if (existingTo === undefined || existingTo < nextLayer) {
        layers.set(edge.To, nextLayer)
        changed = true
      }
    }
    if (!changed) break
  }

  const maxLayer = Math.max(0, ...Array.from(layers.values()))
  for (const id of nodeIds) {
    if (!layers.has(id)) {
      layers.set(id, maxLayer + 1)
    }
  }

  return layers
}

function sortNodesInLayer(a: WorkflowGraphNode, b: WorkflowGraphNode): number {
  const rank = (node: WorkflowGraphNode) => {
    if (node.NodeId === VIRTUAL_END) return 2
    if (node.Kind === 'virtual') return 0
    return 1
  }
  const rankDiff = rank(a) - rank(b)
  if (rankDiff !== 0) return rankDiff
  return a.Label.localeCompare(b.Label)
}

function decideWorkflowLayoutDirection(graph: WorkflowGraph): TopologyLayoutDirection {
  if (graph.GraphTemplate === 'react') return 'horizontal'
  const layers = assignWorkflowLayers(
    graph.Nodes.map(node => node.NodeId),
    graph.Edges
  )
  const layerBuckets = new Map<number, number>()
  layers.forEach(layer => {
    layerBuckets.set(layer, (layerBuckets.get(layer) ?? 0) + 1)
  })
  const layerCount = layerBuckets.size
  const maxBreadth = Math.max(0, ...Array.from(layerBuckets.values()))
  return maxBreadth > layerCount ? 'horizontal' : 'vertical'
}

function buildWorkflowPositions(
  graph: WorkflowGraph,
  direction: TopologyLayoutDirection,
  nodeWidth: number,
  nodeHeight: number,
  gapX: number,
  gapY: number
): Map<string, { x: number; y: number }> {
  const positions = new Map<string, { x: number; y: number }>()
  const nodeIds = graph.Nodes.map(node => node.NodeId)
  const layers = assignWorkflowLayers(nodeIds, graph.Edges)

  const layerNodes = new Map<number, WorkflowGraphNode[]>()
  for (const node of graph.Nodes) {
    const layer = layers.get(node.NodeId) ?? 0
    const list = layerNodes.get(layer) ?? []
    list.push(node)
    layerNodes.set(layer, list)
  }

  const sortedLayers = Array.from(layerNodes.keys()).sort((a, b) => a - b)

  if (direction === 'horizontal') {
    const colWidth = nodeWidth + gapX + 24
    const siblingGap = gapY + 8
    sortedLayers.forEach((layer, layerIndex) => {
      const nodes = [...(layerNodes.get(layer) ?? [])].sort(sortNodesInLayer)
      const totalHeight = nodes.length * nodeHeight + (nodes.length - 1) * siblingGap
      const startY = -totalHeight / 2
      nodes.forEach((node, index) => {
        positions.set(node.NodeId, {
          x: layerIndex * colWidth,
          y: startY + index * (nodeHeight + siblingGap)
        })
      })
    })

    let minY = Infinity
    positions.forEach(pos => {
      minY = Math.min(minY, pos.y)
    })
    if (minY < 0) {
      positions.forEach((pos, id) => {
        positions.set(id, { ...pos, y: pos.y - minY })
      })
    }
    return positions
  }

  const rowHeight = nodeHeight + gapY
  sortedLayers.forEach((layer, layerIndex) => {
    const nodes = [...(layerNodes.get(layer) ?? [])].sort(sortNodesInLayer)
    const totalWidth = nodes.length * nodeWidth + (nodes.length - 1) * gapX
    const startX = -totalWidth / 2
    nodes.forEach((node, index) => {
      positions.set(node.NodeId, {
        x: startX + index * (nodeWidth + gapX),
        y: layerIndex * rowHeight
      })
    })
  })

  let minX = Infinity
  positions.forEach(pos => {
    minX = Math.min(minX, pos.x)
  })
  if (minX < 0) {
    positions.forEach((pos, id) => {
      positions.set(id, { ...pos, x: pos.x - minX })
    })
  }

  return positions
}

/** 双向边（如 llm_call ↔ tool_node）合并为一条，保留双边箭头 */
function mergeBidirectionalWorkflowEdges(edges: WorkflowGraphEdge[]): LangfuseWorkflowEdge[] {
  const pending = edges.map(edge => ({ from: edge.From, to: edge.To, kind: edge.Kind }))
  const result: LangfuseWorkflowEdge[] = []
  const used = new Set<number>()

  for (let i = 0; i < pending.length; i += 1) {
    if (used.has(i)) continue
    const current = pending[i]
    const reverseIndex = pending.findIndex(
      (other, index) =>
        index !== i && !used.has(index) && other.from === current.to && other.to === current.from
    )

    if (reverseIndex !== -1) {
      const reverse = pending[reverseIndex]
      const [from, to] =
        current.from < current.to ? [current.from, current.to] : [current.to, current.from]
      result.push({
        from,
        to,
        kind:
          current.kind === 'conditional' || reverse.kind === 'conditional'
            ? 'conditional'
            : current.kind,
        bidirectional: true
      })
      used.add(i)
      used.add(reverseIndex)
      continue
    }

    result.push({ from: current.from, to: current.to, kind: current.kind })
    used.add(i)
  }

  return result
}

/** Langfuse 拓扑中首个可点击节点（跳过 __start__ / __end__ 等 virtual 节点）对应的 Span */
export function getLangfuseDefaultSelectableSpan(
  graph: WorkflowGraph,
  spans: TraceAnalysisDetailSpan[]
): TraceAnalysisDetailSpan | undefined {
  const spanMap = new Map(spans.map(span => [span.SpanId, span]))
  for (const node of graph.Nodes) {
    if (node.Kind === 'virtual') continue
    const items = resolveWorkflowExecutionItems(graph, node, spanMap)
    if (items.length) return items[0].span
  }
  return undefined
}

export function buildLangfuseWorkflowTopology(
  graph: WorkflowGraph,
  spans: TraceAnalysisDetailSpan[],
  options: TopologyLayoutOptions = {}
): LangfuseWorkflowTopology {
  const nodeWidth = options.nodeWidth ?? 168
  const nodeHeight = options.nodeHeight ?? 60
  const gapX = options.gapX ?? 28
  const gapY = options.gapY ?? 40
  const spanMap = new Map(spans.map(span => [span.SpanId, span]))
  const layoutDirection = decideWorkflowLayoutDirection(graph)
  const positions = buildWorkflowPositions(
    graph,
    layoutDirection,
    nodeWidth,
    nodeHeight,
    gapX,
    gapY
  )

  const nodes: LangfuseWorkflowNode[] = graph.Nodes.map(node => {
    const pos = positions.get(node.NodeId) ?? { x: 0, y: 0 }
    const executionItems = resolveWorkflowExecutionItems(graph, node, spanMap)
    const currentExecutionIndex = resolveInitialExecutionIndex(executionItems)
    const span = findWorkflowExecutionItem(executionItems, currentExecutionIndex)?.span
    return {
      nodeId: node.NodeId,
      label: node.Label || node.NodeId,
      kind: node.Kind,
      span,
      executionItems,
      currentExecutionIndex: executionItems.length ? currentExecutionIndex : undefined,
      executionCount: node.ExecutionCount ?? executionItems.length,
      totalDurationMs: node.TotalDurationMs,
      x: pos.x,
      y: pos.y
    }
  })

  const edges = mergeBidirectionalWorkflowEdges(graph.Edges)

  return { nodes, edges, layoutDirection }
}

/** @deprecated 使用 hasLangfuseWorkflowGraph */
export const hasWorkflowGraph = hasLangfuseWorkflowGraph

/** @deprecated 使用 buildLangfuseWorkflowTopology */
export const buildWorkflowGraphTopology = buildLangfuseWorkflowTopology

export type WorkflowTopologyNode = LangfuseWorkflowNode
export type WorkflowTopologyEdge = LangfuseWorkflowEdge
export type WorkflowTopology = LangfuseWorkflowTopology
export type WorkflowTopologyLayoutOptions = TopologyLayoutOptions
